import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import moment from 'moment';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  fetchHolidayPlanningDetails,
  saveHolidayPlanning,
} from '../../../store/apis/holidayService';
import { STATUS_COLOR, STATUS_BORDER_COLOR } from './constant';
import { getAuthUser } from '../../../utils/auth.util';
import Legend from './components/Legend';
import { SpinnerLoading } from '../../Common';

import './HolidayPlanningCalendar.scss';

const months = moment.months();
const days = Array.from({ length: 31 }, (_, i) => i + 1);
const getKey = (mIdx, day) => `${mIdx}-${day}`;

export default function HolidayPlanningCalendar() {
  const { employeeId } = getAuthUser();
  const queryClient = useQueryClient();
  const thisYear = new Date().getFullYear();

  const [statusMap, setStatusMap] = useState({});
  const [eventMap, setEventMap] = useState({});
  const [selections, setSelections] = useState({});
  const initializedRef = useRef(false);

  const WEEKEND_OVERRIDES_LEAVE = true;

  const { data, isLoading } = useQuery({
    queryKey: ['holidayPlanningDetails', thisYear],
    queryFn: () =>
      fetchHolidayPlanningDetails({
        year: thisYear,
        departmentId: '',
        section: '',
        employeeId: '',
        managerId: employeeId,
        fromDate: `${thisYear}-01-01 00:00:00`,
        toDate: `${thisYear}-12-31 00:00:00`,
        isDirectReport: 0,
        isViewAllEmployee: 0,
      }),
  });

  const saveHolidayPlanningMutation = useMutation({
    mutationFn: payload => saveHolidayPlanning(payload),
    onSuccess: resp => {
      if (resp.flag === 1) {
        queryClient.invalidateQueries(['holidayPlanningDetails', thisYear]);
        toast.success('Holiday Planning Saved Successfully!', {
          className: 'toast-success',
        });
      }
    },
  });

  useEffect(() => {
    if (!data?.dataset) return;

    const sm = {};
    const em = {};

    // 1) actual leaves (annual, sick, workFromHome, other)
    data.dataset[0].forEach(r => {
      const from = moment(r.LvApprFromDt);
      const to = moment(r.LvApprToDt);
      const desc = (r.LeaveDesc || '').toLowerCase();
      let statusType;
      if (desc.includes('annual leave')) statusType = 'annual';
      else if (desc.includes('sick leave')) statusType = 'sick';
      else if (desc.includes('work from home')) statusType = 'workFromHome';
      else statusType = 'other';

      for (let m = from.clone(); m.isSameOrBefore(to, 'day'); m.add(1, 'day')) {
        const key = getKey(m.month(), m.date());
        sm[key] = sm[key] ? [...sm[key], statusType] : [statusType];
        em[key] = em[key] ? [...em[key], r] : [r];
      }
    });

    // 2) holidays
    data.dataset[2].forEach(h => {
      const mIdx = parseInt(h.Mm, 10) - 1;
      const d = parseInt(h.Dd, 10);
      const key = getKey(mIdx, d);
      sm[key] = sm[key] ? [...sm[key], 'holiday'] : ['holiday'];
      em[key] = em[key] ? [...em[key], h] : [h];
    });

    // 3) weekends override
    const weekends =
      data.dataset[1]?.[0]?.Weekend?.split('~').map(s => s.trim()) || [];
    months.forEach((_, mIdx) => {
      const dim = moment().year(thisYear).month(mIdx).daysInMonth();
      for (let d = 1; d <= dim; d += 1) {
        const key = getKey(mIdx, d);
        const weekday = moment({
          year: thisYear,
          month: mIdx,
          day: d,
        }).format('dddd');
        if (weekends.includes(weekday)) {
          const st = 'weekend';
          if (WEEKEND_OVERRIDES_LEAVE) {
            sm[key] = sm[key] ? [st, ...sm[key]] : [st];
          } else if (!sm[key]) {
            sm[key] = [st];
          }
        }
      }
    });

    setStatusMap(sm);
    setEventMap(em);

    // 4) initialize planned selections once
    if (!initializedRef.current) {
      const initSel = {};
      data.dataset[3].forEach(p => {
        const m = moment(p.PlanDt);
        initSel[getKey(m.month(), m.date())] = 'planned';
      });
      setSelections(initSel);
      initializedRef.current = true;
    }
  }, [data, thisYear]);

  const toggleCell = (mIdx, day) => {
    const key = getKey(mIdx, day);
    setSelections(prev => {
      if (prev[key]) {
        return Object.fromEntries(
          Object.entries(prev).filter(([k]) => k !== key),
        );
      }
      return { ...prev, [key]: 'planned' };
    });
  };

  const buildTooltipText = evt => {
    if (!evt) return '';
    if (evt.LeaveDesc) {
      return `${evt.LeaveDesc} (${moment(evt.LvApprFromDt).format(
        'YYYY-MM-DD',
      )} - ${moment(evt.LvApprToDt).format('YYYY-MM-DD')})`;
    }
    if (evt.HolidayDesc) return evt.HolidayDesc;
    return '';
  };

  const renderCell = (mIdx, day) => {
    const dim = moment().year(thisYear).month(mIdx).daysInMonth();
    const key = getKey(mIdx, day);

    // 1) out-of-month?
    const isOutOfMonth = day > dim;

    // 2) raw statuses + planned
    const raw = statusMap[key] ? [...statusMap[key]] : [];
    if (selections[key]) raw.push('planned');

    // 3) priority list
    const priority = [
      'weekend',
      'annual',
      // 'sick',
      // 'workFromHome',
      'holiday',
      'planned',
    ];

    // 4) known vs unknown
    const known = priority.filter(s => raw.includes(s));
    const unknown = raw.filter(s => !priority.includes(s));

    // 5) primary & secondaries
    const primary = known[0] || null;
    const secondaries = known.slice(1);

    // 6) build tooltip
    const labelMap = {
      weekend: 'Weekend',
      annual: 'Annual Leave',
      sick: 'Sick Leave',
      workFromHome: 'Work From Home',
      holiday: 'Holiday',
      planned: 'Planned',
      // other: 'Other',
    };
    const lines = [];

    // wrap eventMap[key] into array safely
    let events = [];
    if (Array.isArray(eventMap[key])) {
      events = eventMap[key];
    } else if (eventMap[key]) {
      events = [eventMap[key]];
    }

    // known statuses
    known.forEach(s => {
      if (s === 'weekend' || s === 'planned') {
        lines.push(labelMap[s]);
      } else {
        events
          .filter(evt => {
            const d = (evt.LeaveDesc || '').toLowerCase();
            if (s === 'annual') return d.includes('annual leave');
            if (s === 'sick') return d.includes('sick leave');
            if (s === 'workFromHome') return d.includes('work from home');
            if (s === 'holiday') return !!evt.HolidayDesc;
            return false;
          })
          .forEach(evt => lines.push(buildTooltipText(evt)));
      }
    });

    // unknown under “Other”
    if (unknown.length) {
      lines.push(labelMap.other);
      events
        .filter(evt => {
          const d = (evt.LeaveDesc || '').toLowerCase();
          return (
            !['annual leave', 'sick leave', 'work from home'].some(k =>
              d.includes(k),
            ) && !!evt.LeaveDesc
          );
        })
        .forEach(evt => lines.push(buildTooltipText(evt)));
    }

    const tooltipText = lines.join('\n');

    // 7) clickability & background
    const isClickable =
      !isOutOfMonth && !['annual', 'holiday', 'weekend'].includes(primary);
    let tdBg;
    if (isOutOfMonth) {
      tdBg = '#E6E6E6';
    } else if (primary === 'weekend') {
      tdBg = STATUS_COLOR.weekend;
    } else {
      tdBg = 'transparent';
    }

    return (
      <td
        key={day}
        className={`cell${isOutOfMonth ? ' na' : ''}`}
        onClick={() => isClickable && toggleCell(mIdx, day)}
        title={tooltipText}
        style={{
          cursor: isClickable ? 'pointer' : 'default',
          padding: 0,
          backgroundColor: tdBg,
        }}
      >
        {/* primary highlight (non-weekend/out-of-month) */}
        {primary && primary !== 'weekend' && !isOutOfMonth && (
          <div
            className="highlighted-cell"
            style={{
              backgroundColor: STATUS_COLOR[primary],
              border: `2px solid ${STATUS_BORDER_COLOR[primary]}`,
            }}
          />
        )}
        {/* badges */}
        {!isOutOfMonth &&
          secondaries.map((s, i) => (
            <span
              key={i}
              className={`status-badge status-${s}`}
              title={labelMap[s]}
            />
          ))}
      </td>
    );
  };

  const handleSavePlanDates = () => {
    const planDateList = Object.keys(selections).map(key => {
      const [mIdx, d] = key.split('-').map(Number);
      return {
        planDate: moment({
          year: thisYear,
          month: mIdx,
          day: d,
        }).format('YYYY-MM-DD'),
      };
    });
    saveHolidayPlanningMutation.mutate({ year: thisYear, planDateList });
  };

  if (isLoading) {
    return <SpinnerLoading spinnerSize="2rem" height="85vh" />;
  }

  const plannedDays = Object.keys(selections).length;
  const annualDaysCount = Object.entries(statusMap).filter(
    ([, statuses]) => statuses[0] === 'annual',
  ).length;

  return (
    <div className="d-flex flex-column planner">
      <div className="main-header mb-2 py-2">
        <h4 className="fw-semibold">Holiday Planning Calendar – {thisYear}</h4>
      </div>
      <div className="d-flex justify-content-between px-3">
        <Legend />
        <div>
          <span className="planned-leave-header">Total Planned Days: </span>
          <span className="planned-leave-count">
            {annualDaysCount + plannedDays}
          </span>
        </div>
      </div>

      <div className="holiday-planner d-flex flex-column px-3 flex-grow-1 py-2">
        <div className="calendar-table">
          <table>
            <thead>
              <tr>
                <th className="table-header">Month</th>
                {days.map(d => (
                  <th className="table-header" key={d}>
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {months.map((month, idx) => (
                <tr key={month}>
                  <td
                    className="month-cell"
                    style={{
                      borderBottom:
                        idx === months.length - 1
                          ? 'none'
                          : '1px solid #37bef9',
                    }}
                  >
                    {month}
                  </td>
                  {days.map(day => renderCell(idx, day))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="px-3 d-flex justify-content-end gap-2">
        <Button
          className="custom-light-btn"
          style={{
            width: '20%',
          }}
          onClick={() => setSelections({})}
        >
          Reset
        </Button>
        <Button
          className="custom-btn"
          style={{
            width: '20%',
          }}
          onClick={handleSavePlanDates}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
